import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import CodeMirror from '@uiw/react-codemirror'
import { python } from '@codemirror/lang-python'
import { indentUnit } from '@codemirror/language'
import { Transaction } from '@codemirror/state'
import { EditorView, ViewPlugin } from '@codemirror/view'
import { githubLight } from '@uiw/codemirror-theme-github'
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
} from '@mui/material'

import Markdown from '../../study/components/Markdown'
import { AuthContext } from '../../context/AuthContext'
import { SessionContext } from '../../context/SessionContext'
import { saveTelemetry } from '../workspace/service/telemetryService'
import { editorKeyPayload, mainSelectionPayload, studyEditorTelemetry } from '../workspace/components/CodeEditor'
import {
  CURSOR_DEBOUNCE_MS, KEYSTROKE_EVENTS, capChanges, textChange, textareaSelection, trimStrings,
} from './keystrokes'
import usePyodideWorker, { cleanPythonError } from './usePyodideWorker'
import QuestionForm from './QuestionForm'
import ReasoningGapChat from './ReasoningGapChat'
import { FEEDBACK_RATING, aiUseSurveyFor, isComplete, visibleAnswers } from './surveyQuestions'
import {
  getReasoningGapTask, submitReasoningGap, getReasoningGapSubmission, reportReasoningGapTests,
  rateReasoningGapFeedback, submitReasoningGapSurvey, resubmitReasoningGap,
} from '../../service/reasoningGapService'
import '../workspace/css/workspace.scss'
import './reasoningGap.scss'

// Student view for GapCode / GapThinking sessions. The two conditions render identically -
// nothing here knows or reveals which ground truth the server compares against.
//
// Flow: write code + explanation -> Submit -> hidden tests run here in Pyodide -> server
// analyzes (polled) -> hints -> hint rating -> either "Revise and resubmit" (back to the
// workspace with the last code, explanation and hints; up to maxAttempts) or "I'm done"
// -> AI-use survey -> done.

const SNAPSHOT_DEBOUNCE_MS = 4000
const FLUSH_INTERVAL_MS = 5000
const PASTE_TEXT_LIMIT = 4000
// Chrome drops a keepalive request over about 64KB without an error; and a batch is sent once
// it has this many events (about 200 bytes each when typing).
const KEEPALIVE_MAX_BYTES = 60000
const MAX_BATCH_EVENTS = 120
// Sent as soon as they're logged rather than on the next interval, so a reload or a closed
// tab right after one of these never loses it.
const MILESTONES = new Set([
  'GAP_SUBMIT', 'GAP_TESTS_RUN', 'GAP_FEEDBACK_VIEWED', 'GAP_FEEDBACK_RATED', 'GAP_REVISE_START',
  'GAP_FINISH_CHOSEN', 'GAP_SURVEY_SUBMITTED', 'CHAT_QUERY', 'CHAT_RESPONSE', 'CHAT_ERROR',
])
// "session:user" pairs that already logged GAP_SESSION_START on this page load, so a remount
// of the component doesn't log a second start.
const startedThisLoad = new Set()
// "session:user:attempt" keys whose feedback view was already logged on this page load.
const feedbackViewedThisLoad = new Set()

// Per-viewer draft/timer persistence so a refresh never loses work. Storage can be missing
// or throw (private windows, blocked site data), so every access is guarded.
function readStore (key) {
  try { return JSON.parse(window.localStorage.getItem(key)) } catch (e) { return null }
}
function writeStore (key, value) {
  try { window.localStorage.setItem(key, JSON.stringify(value)) } catch (e) { /* ignore */ }
}

export default function ReasoningGapStudent () {
  const { currentUser } = useContext(AuthContext)
  const { session } = useContext(SessionContext)
  const sessionId = session?.id
  const userId = currentUser?.id
  const storeKey = sessionId && userId ? `rg:${sessionId}:${userId}` : null

  const [task, setTask] = useState(null)
  const aiUseSurvey = aiUseSurveyFor(task?.surveyConceptOption)
  const [submission, setSubmission] = useState(null)
  const [loadError, setLoadError] = useState('')
  const [code, setCode] = useState('')
  const [explanation, setExplanation] = useState('')
  const [output, setOutput] = useState('')
  const [running, setRunning] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [actionError, setActionError] = useState('')
  const [rating, setRating] = useState({})
  const [survey, setSurvey] = useState({})
  const [savingForm, setSavingForm] = useState(false)
  // Revising after feedback (back in the workspace) / finishing (on to the survey).
  const [revising, setRevising] = useState(false)
  const [finishing, setFinishing] = useState(false)
  const startedAtRef = useRef(null)
  // Latest editor contents for the assistant, read at send time without re-rendering it.
  const codeRef = useRef('')
  const explanationRef = useRef('')
  codeRef.current = code
  explanationRef.current = explanation
  // Attempt number whose hidden tests have been started, so each attempt runs them once.
  const testsRunForRef = useRef(null)
  // When the current attempt's work began (first visit, or the moment revising started).
  const attemptStartedRef = useRef(null)
  const pyodide = usePyodideWorker()

  // ── Telemetry ────────────────────────────────────────────────────────────────
  // Same batch endpoint and event envelope as the classroom workspace, so the existing
  // export/replay tooling can read these rows. Paste and focus events are the main
  // behavioral evidence of outside (e.g. AI) help alongside the self-reported survey.
  const bufferRef = useRef([])
  const snapshotTimer = useRef(null)
  const flushRef = useRef(() => {})
  const log = useCallback((type, payload = {}) => {
    bufferRef.current.push({ type, payload, timestamp: Date.now(), sessionId: sessionId ? +sessionId : null })
    // A burst of keystrokes is sent before the interval too, so no batch outgrows a keepalive request.
    if (MILESTONES.has(type) || bufferRef.current.length === MAX_BATCH_EVENTS) setTimeout(() => flushRef.current(), 0)
  }, [sessionId])
  // how: 'unload' when the page is going away (reload, close, navigation), so send with
  // keepalive, which the browser completes after the page is gone; an axios request would be
  // cancelled. 'hidden' when the tab was hidden, which is also the first step of a close: keepalive
  // when the batch is within half the cap (Chrome's cap covers every keepalive request still in
  // flight, so the pagehide batch that may follow keeps the other half), else a normal request.
  const flush = useCallback((how) => {
    if (!bufferRef.current.length || !sessionId || !userId) return
    const events = bufferRef.current
    bufferRef.current = []
    if (how === 'hidden') {
      try {
        const body = JSON.stringify({ sessionId, userId, events })
        if (body.length <= KEEPALIVE_MAX_BYTES / 2) {
          fetch(`${process.env.REACT_APP_HOST_API}/telemetry/events`, {
            method: 'POST',
            keepalive: true,
            headers: { 'Content-Type': 'application/json' },
            body,
          }).catch(() => {})
          return
        }
      } catch (e) {
        // keepalive unsupported: fall through to the normal request.
      }
    }
    if (how === 'unload') {
      try {
        // A keepalive request is limited to about 64KB in Chrome and is silently dropped when
        // larger (QA 2026-10-29: an 85KB paste lost its events). Shorten long text fields until
        // the batch fits; the full code is still in the snapshots sent while the page was open.
        let body = JSON.stringify({ sessionId, userId, events })
        let trimmed = events
        for (let limit = 8000; body.length > KEEPALIVE_MAX_BYTES && limit >= 250; limit = Math.floor(limit / 2)) {
          trimmed = events.map(e => ({ ...e, payload: trimStrings(e.payload || {}, limit) }))
          body = JSON.stringify({ sessionId, userId, events: trimmed })
        }
        // Still too large (a long burst of typing): the keystroke events go, so the rest arrive.
        if (body.length > KEEPALIVE_MAX_BYTES) {
          const kept = trimmed.filter(e => !KEYSTROKE_EVENTS.has(e.type))
          const dropped = trimmed.length - kept.length
          if (dropped) kept.push({ type: 'GAP_KEYSTROKES_DROPPED', payload: { count: dropped }, timestamp: Date.now(), sessionId: +sessionId })
          body = JSON.stringify({ sessionId, userId, events: kept })
        }
        fetch(`${process.env.REACT_APP_HOST_API}/telemetry/events`, {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/json' },
          body,
        }).catch(() => {})
        return
      } catch (e) {
        // keepalive unsupported or body too large: fall through to the normal request.
      }
    }
    saveTelemetry(sessionId, userId, events)
  }, [sessionId, userId])
  flushRef.current = flush

  useEffect(() => {
    if (!sessionId || !userId) return undefined
    const startKey = `${sessionId}:${userId}`
    if (!startedThisLoad.has(startKey)) {
      startedThisLoad.add(startKey)
      log('GAP_SESSION_START', { userAgent: navigator.userAgent })
    }
    const interval = setInterval(flush, FLUSH_INTERVAL_MS)
    const onVisibility = () => {
      log('TAB_STATE', { visible: document.visibilityState === 'visible' })
      // Closing a tab hides it first, so this batch goes with keepalive when it fits; pagehide
      // below sends whatever is left as the page actually goes away.
      if (document.visibilityState === 'hidden') flush('hidden')
    }
    const onBlur = () => log('GAP_WINDOW_BLUR')
    const onFocus = () => log('GAP_WINDOW_FOCUS')
    const onPageHide = () => {
      // Write out a snapshot that was still waiting on its debounce, then send everything.
      if (snapshotTimer.current) {
        clearTimeout(snapshotTimer.current)
        snapshotTimer.current = null
        log('GAP_SNAPSHOT', { code: codeRef.current, explanation: explanationRef.current })
      }
      flush('unload')
    }
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('blur', onBlur)
    window.addEventListener('focus', onFocus)
    window.addEventListener('pagehide', onPageHide)
    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('blur', onBlur)
      window.removeEventListener('focus', onFocus)
      window.removeEventListener('pagehide', onPageHide)
      flush()
    }
  }, [sessionId, userId, log, flush])

  // Debounced full-text snapshots of both inputs, for reconstructing how work evolved.
  const scheduleSnapshot = useCallback((nextCode, nextExplanation) => {
    clearTimeout(snapshotTimer.current)
    snapshotTimer.current = setTimeout(() => {
      snapshotTimer.current = null
      log('GAP_SNAPSHOT', { code: nextCode, explanation: nextExplanation })
    }, SNAPSHOT_DEBOUNCE_MS)
  }, [log])
  useEffect(() => () => clearTimeout(snapshotTimer.current), [])

  const logPaste = useCallback((target, text) => {
    log('CODE_PASTE', { target, length: text.length, text: text.slice(0, PASTE_TEXT_LIMIT) })
  }, [log])

  // Keystroke logging must never get in the way of typing.
  const logInput = useCallback((type, payload) => {
    try { log(type, payload) } catch (e) { /* ignore */ }
  }, [log])

  const editorExtensions = useMemo(() => [
    python(),
    indentUnit.of('    '),
    EditorView.domEventHandlers({
      paste: (event) => {
        logPaste('code', event.clipboardData ? event.clipboardData.getData('text') : '')
        return false
      },
    }),
    // action is CodeMirror's user event (input.type, delete.backward, input.paste, undo...), or
    // 'set' when the page replaced the text (starter code, a restored draft).
    EditorView.updateListener.of((update) => {
      if (!update.docChanged) return
      try {
        const tr = update.transactions.find(t => t.annotation(Transaction.userEvent))
        const changes = []
        update.changes.iterChanges((fromA, toA, _fromB, _toB, inserted) => changes.push({ from: fromA, to: toA, text: inserted.toString() }))
        logInput('GAP_EDIT', {
          target: 'code',
          action: tr ? tr.annotation(Transaction.userEvent) : 'set',
          changes: capChanges(changes),
          selection: mainSelectionPayload(update.state.selection),
        })
      } catch (e) { /* ignore */ }
    }),
    ...studyEditorTelemetry((type, payload) => logInput(type, { target: 'code', ...payload }), () => undefined),
    // The text the editor opened with, so its GAP_EDITs can be replayed from it.
    ViewPlugin.define((view) => {
      logInput('GAP_EDITOR_TEXT', { target: 'code', text: view.state.doc.toString() })
      return {}
    }),
  ], [logPaste, logInput])

  // The same for the explanation box, each time it appears (first load, and each revision).
  const explanationBoxRef = useCallback((el) => {
    if (el) logInput('GAP_EDITOR_TEXT', { target: 'explanation', text: el.value })
  }, [logInput])

  // The explanation box: the selection last recorded (by an edit or a cursor event), and a
  // cursor move waiting for CURSOR_DEBOUNCE_MS of rest.
  const explanationSelRef = useRef(null)
  const explanationPending = useRef(null)
  const flushExplanationCursor = useCallback(() => {
    const pending = explanationPending.current
    clearTimeout(pending?.timer)
    explanationPending.current = null
    const last = explanationSelRef.current
    if (!pending || (last && last.anchor === pending.selection.anchor && last.head === pending.selection.head)) return
    explanationSelRef.current = pending.selection
    logInput('CURSOR_SELECT', { target: 'explanation', ranges: [pending.selection], main: 0, timestamp: pending.at })
  }, [logInput])
  useEffect(() => () => clearTimeout(explanationPending.current?.timer), [])
  const onExplanationCursor = (e) => {
    try {
      clearTimeout(explanationPending.current?.timer)
      explanationPending.current = {
        selection: textareaSelection(e.target),
        at: Date.now(),
        timer: setTimeout(flushExplanationCursor, CURSOR_DEBOUNCE_MS),
      }
    } catch (err) { /* ignore */ }
  }
  const onExplanationKey = (e) => {
    try {
      const payload = editorKeyPayload(e.nativeEvent)
      if (payload) logInput('EDITOR_KEY', { target: 'explanation', ...payload })
    } catch (err) { /* ignore */ }
  }
  const onExplanationInput = (e) => {
    const el = e.target
    const before = explanationRef.current
    onExplanationChange(el.value)
    try {
      // A cursor move made just before this edit is logged first, against the text it was made in.
      flushExplanationCursor()
      const selection = textareaSelection(el)
      explanationSelRef.current = selection
      logInput('GAP_EDIT', {
        target: 'explanation',
        action: (e.nativeEvent && e.nativeEvent.inputType) || 'input',
        changes: capChanges([textChange(before, el.value)]),
        selection,
      })
    } catch (err) { /* ignore */ }
  }

  // ── Load ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!sessionId || !userId) return
    let cancelled = false
    getReasoningGapTask(sessionId, userId)
      .then((data) => {
        if (cancelled) return
        // This account joined the other group's session first (another device or browser, where
        // the shared link remembered nothing): send them back to their own group.
        if (data.groupSessionId && String(data.groupSessionId) !== String(sessionId)) {
          window.location.replace(`/session/${data.groupSessionId}`)
          return
        }
        setTask(data.task)
        setSubmission(data.submission)
        const saved = storeKey ? readStore(storeKey) : null
        setCode(saved?.code ?? data.task.starterCode)
        setExplanation(saved?.explanation ?? '')
        startedAtRef.current = saved?.startedAt ?? Date.now()
        attemptStartedRef.current = saved?.attemptStartedAt ?? startedAtRef.current
        if (storeKey && !saved?.startedAt) writeStore(storeKey, { startedAt: startedAtRef.current })
        // A reload while revising returns to the workspace instead of the feedback screen.
        const sub = data.submission
        if (sub && saved?.revisingAttempt === sub.attempt && sub.canResubmit) setRevising(true)
      })
      .catch(e => !cancelled && setLoadError(e.response?.data?.error || e.message))
    return () => { cancelled = true }
  }, [sessionId, userId, storeKey])

  const persistDraft = (nextCode, nextExplanation, extra = {}) => {
    if (!storeKey) return
    const saved = readStore(storeKey) || {}
    writeStore(storeKey, { ...saved, startedAt: startedAtRef.current, code: nextCode, explanation: nextExplanation, ...extra })
  }

  const onCodeChange = (value) => {
    setCode(value)
    persistDraft(value, explanation)
    scheduleSnapshot(value, explanation)
  }
  const onExplanationChange = (value) => {
    setExplanation(value)
    persistDraft(code, value)
    scheduleSnapshot(code, value)
  }

  // ── Run (student's own __main__ block only; hidden tests never run here) ──────
  const onRun = async () => {
    setRunning(true)
    setOutput('')
    const result = await pyodide.run(code)
    const text = result.ok
      ? (result.stdout || '(no output)')
      : `${result.stdout || ''}${cleanPythonError(result.error)}`
    setOutput(text)
    log('GAP_RUN', { ok: !!result.ok, timedOut: !!result.timedOut, code, output: text.slice(0, 2000) })
    setRunning(false)
  }

  // ── Submit ───────────────────────────────────────────────────────────────────
  // Not while a Run is in progress: its timeout restarts the Python worker, which would also
  // kill the hidden tests queued behind it and score correct code 0/9 (QA 2026-10-29).
  const canSubmit = code.trim() && explanation.trim().length >= 20 && !running
  const nextAttempt = revising && submission ? submission.attempt + 1 : 1
  const onSubmit = async () => {
    setSubmitting(true)
    setActionError('')
    try {
      // Time spent on this attempt (since the page first opened, or since revising began).
      const timeOnTaskMs = attemptStartedRef.current ? Date.now() - attemptStartedRef.current : null
      log('GAP_SUBMIT', { code, explanation, timeOnTaskMs, attempt: nextAttempt })
      flush()
      const next = revising
        ? await resubmitReasoningGap(submission.id, { userId, code, explanation, timeOnTaskMs })
        : await submitReasoningGap(sessionId, { userId, code, explanation, timeOnTaskMs })
      setSubmission(next)
      setRevising(false)
      setRating({})
      persistDraft(code, explanation, { revisingAttempt: null })
      setConfirmOpen(false)
    } catch (e) {
      setActionError(e.response?.data?.error || 'Could not submit. Check your connection and try again.')
    } finally {
      setSubmitting(false)
    }
  }

  // ── Hidden tests: run once, automatically, as soon as the locked submission exists ──
  useEffect(() => {
    if (!submission || submission.status !== 'submitted' || !submission.testInputs || testsRunForRef.current === submission.attempt) return
    testsRunForRef.current = submission.attempt
    ;(async () => {
      const result = await pyodide.runTests(submission.code, submission.testInputs, task?.functionName || 'best_split')
      let payload
      if (result.loadError) {
        payload = { userId, harnessError: result.error }
      } else if (result.ok) {
        payload = { userId, results: result.results }
      } else {
        // The module itself failed (syntax error, top-level exception, infinite loop):
        // every hidden test fails with that error.
        const error = result.timedOut ? 'timed out' : cleanPythonError(result.error).split('\n').slice(-1)[0]
        payload = { userId, results: submission.testInputs.map(t => ({ id: t.id, error })) }
      }
      log('GAP_TESTS_RUN', { ok: !!result.ok, timedOut: !!result.timedOut, harnessError: payload.harnessError || null })
      try {
        setSubmission(await reportReasoningGapTests(submission.id, payload))
      } catch (e) {
        testsRunForRef.current = null
        setActionError('Could not record your results. Reload the page to retry.')
      }
    })()
  }, [submission, pyodide, userId, log, task?.functionName])

  // ── Poll while the analysis runs ─────────────────────────────────────────────
  const status = submission?.status
  useEffect(() => {
    if (!submission || !['tested', 'analyzing', 'analysis_failed'].includes(status)) return undefined
    const delay = status === 'analysis_failed' ? 20000 : 3000
    const timer = setTimeout(async () => {
      try {
        setSubmission(await getReasoningGapSubmission(submission.id, userId))
      } catch (e) {
        // Transient - try again on the next tick.
        setSubmission(s => ({ ...s }))
      }
    }, delay)
    return () => clearTimeout(timer)
  }, [submission, status, userId])

  // Logged when an attempt's feedback is actually on screen (not while revising), once per
  // attempt per page load.
  useEffect(() => {
    if (status !== 'analyzed' || revising || !submission) return
    const key = `${sessionId}:${userId}:${submission.attempt}`
    if (feedbackViewedThisLoad.has(key)) return
    feedbackViewedThisLoad.add(key)
    log('GAP_FEEDBACK_VIEWED', { attempt: submission.attempt, hints: submission.feedback?.hints?.length || 0 })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, revising, submission?.attempt])

  const saveRating = async () => {
    setSavingForm(true)
    setActionError('')
    try {
      await rateReasoningGapFeedback(submission.id, userId, visibleAnswers(FEEDBACK_RATING, rating))
      log('GAP_FEEDBACK_RATED', { attempt: submission.attempt })
      setSubmission(s => ({ ...s, feedbackRated: true }))
      return true
    } catch (e) {
      setActionError('Could not save your answers. Try again.')
      return false
    } finally {
      setSavingForm(false)
    }
  }

  const saveSurvey = async () => {
    setSavingForm(true)
    setActionError('')
    try {
      await submitReasoningGapSurvey(submission.id, userId, visibleAnswers(aiUseSurvey, survey))
      log('GAP_SURVEY_SUBMITTED')
      flush()
      setSubmission(s => ({ ...s, surveyDone: true }))
    } catch (e) {
      setActionError('Could not save your answers. Try again.')
    } finally {
      setSavingForm(false)
    }
  }

  // Back to the workspace for another attempt, starting from the last submission (or the
  // student's unsent edits, if they had started revising before a reload).
  const startRevising = () => {
    const saved = storeKey ? readStore(storeKey) : null
    const resume = saved?.revisingAttempt === submission.attempt
    const nextCode = resume && saved?.code ? saved.code : submission.code
    const nextExplanation = resume && saved?.explanation ? saved.explanation : submission.explanation
    attemptStartedRef.current = resume && saved?.attemptStartedAt ? saved.attemptStartedAt : Date.now()
    setCode(nextCode)
    setExplanation(nextExplanation)
    setOutput('')
    persistDraft(nextCode, nextExplanation, { revisingAttempt: submission.attempt, attemptStartedAt: attemptStartedRef.current })
    log('GAP_REVISE_START', { fromAttempt: submission.attempt })
    setRevising(true)
  }

  // The hint rating is asked once per attempt, before choosing what to do next.
  const rateThen = async (next) => {
    if (status === 'analyzed' && !submission.feedbackRated) {
      const ok = await saveRating()
      if (!ok) return
    }
    if (next === 'revise') startRevising()
    else {
      log('GAP_FINISH_CHOSEN', { attempt: submission.attempt })
      setFinishing(true)
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  if (loadError) return <div className="rg-center"><Alert severity="error">{loadError}</Alert></div>
  if (!task) return <div className="rg-center"><CircularProgress /></div>

  const lastFeedback = submission && submission.feedback
  if (!submission || revising) {
    return (
      <div className="ws-container rg-student">
        <div className="ws-content">
          <aside className="ws-panel-left rg-student__task">
            <div className="ws-panel-content">
              <div className="rg-task-md"><Markdown text={task.markdown} /></div>
            </div>
          </aside>

          <main className="ws-middle-panel rg-student__main">
            {revising && (
              <details className="rg-revise" open>
                <summary>
                  <strong>Attempt {nextAttempt} of {submission.maxAttempts}</strong>: revise your code and explanation, then submit again.
                  {submission.testsTotal != null && <span className="rg-muted"> Last attempt: {submission.testsPassed} of {submission.testsTotal} hidden inputs handled correctly.</span>}
                </summary>
                {lastFeedback && lastFeedback.hints && lastFeedback.hints.length > 0 && (
                  <ol className="rg-feedback__hints">
                    {lastFeedback.hints.map((h, i) => <li key={i}>{h.hint}</li>)}
                  </ol>
                )}
              </details>
            )}
            <div className="rg-student__bar">
              <span className="ws-section-title">Your Code</span>
              <span className="rg-muted">
                {pyodide.status === 'loading' && 'Loading Python…'}
                {pyodide.status === 'ready' && 'Python 3 · runs in your browser'}
                {pyodide.status === 'error' && 'Python failed to load - reload the page'}
              </span>
            </div>
            <div className="rg-student__editor">
              <CodeMirror value={code} height="100%" theme={githubLight} extensions={editorExtensions} onChange={onCodeChange} />
            </div>

            <div className="ws-terminal-controls">
              <div className="ws-controls-left">
                <button className="ws-run-button" onClick={onRun} disabled={running || pyodide.status === 'error'}>
                  {running ? 'Running…' : 'Run'}
                </button>
                <span className="rg-muted">Runs your file and checks your function against each example (allowing for rounding).</span>
              </div>
            </div>
            <div className="rg-student__output">
              <div className="ws-output-content">{output || 'Click Run to execute your file.'}</div>
            </div>

            <div className="rg-student__explain">
              <span className="ws-section-title">Explain your thinking</span>
              <p className="rg-muted">{task.explanationPrompt}</p>
              <textarea
                ref={explanationBoxRef}
                className="rg-student__textarea"
                value={explanation}
                onChange={onExplanationInput}
                onPaste={e => logPaste('explanation', e.clipboardData.getData('text'))}
                onKeyDown={onExplanationKey}
                onSelect={onExplanationCursor}
                onKeyUp={onExplanationCursor}
                onMouseUp={onExplanationCursor}
                onFocus={onExplanationCursor}
                placeholder="Describe your approach in your own words…"
              />
              <div className="rg-student__submit">
                <span className="rg-muted">
                  {!canSubmit
                    ? 'Write your code and a few sentences explaining your thinking to submit.'
                    : `After submitting you'll get feedback, and can revise and resubmit (up to ${submission ? submission.maxAttempts : 5} attempts).`}
                </span>
                <button className="ws-run-button" disabled={!canSubmit} onClick={() => setConfirmOpen(true)}>Submit</button>
              </div>
            </div>
          </main>

          <aside className="ws-panel-right rg-student__chat">
            <ReasoningGapChat
              sessionId={sessionId}
              userId={userId}
              getCode={() => codeRef.current}
              getExplanation={() => explanationRef.current}
              log={log}
            />
          </aside>
        </div>

        <Dialog className="rg-dialog" open={confirmOpen} onClose={() => !submitting && setConfirmOpen(false)}>
          <DialogTitle>{nextAttempt > 1 ? `Submit attempt ${nextAttempt}?` : 'Submit your answer?'}</DialogTitle>
          <DialogContent>
            Your code will be checked against hidden inputs, and you'll then get hints about any gaps.
            After reading them you can revise and submit again
            {submission ? ` (attempt ${nextAttempt} of ${submission.maxAttempts})` : ' (up to 5 attempts)'}, or finish.
            {actionError && <Alert severity="error" sx={{ mt: 2 }}>{actionError}</Alert>}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)} disabled={submitting}>Keep working</Button>
            <Button variant="contained" onClick={onSubmit} disabled={submitting}>
              {submitting ? <CircularProgress size={20} /> : 'Submit'}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    )
  }

  const feedback = submission.feedback
  const betweenAttempts = status === 'analyzed' || status === 'analysis_failed'
  const choosing = betweenAttempts && !submission.surveyDone && !finishing
  return (
    <div className="rg-after">
      <h2 className="rg-after__title">{task.title}</h2>
      {actionError && <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>}

      <details className="rg-card rg-submitted">
        <summary>Your submitted answer (attempt {submission.attempt})</summary>
        <div className="rg-submitted__label">Code</div>
        <pre className="rg-code">{submission.code}</pre>
        <div className="rg-submitted__label">Explanation</div>
        <p className="rg-submitted__text">{submission.explanation}</p>
      </details>

      {submission.history && submission.history.length > 0 && (
        <details className="rg-card rg-submitted">
          <summary>Earlier attempts ({submission.history.length})</summary>
          {submission.history.map(h => (
            <div key={h.attempt} className="rg-history">
              <div className="rg-submitted__label">Attempt {h.attempt}{h.testsTotal != null ? `: ${h.testsPassed} of ${h.testsTotal} hidden inputs` : ''}</div>
              {h.feedback && h.feedback.hints && h.feedback.hints.length > 0
                ? <ol className="rg-feedback__hints">{h.feedback.hints.map((x, i) => <li key={i}>{x.hint}</li>)}</ol>
                : <p className="rg-muted">No hints for this attempt.</p>}
            </div>
          ))}
        </details>
      )}

      {['submitted', 'tested', 'analyzing'].includes(status) && (
        <div className="rg-card rg-wait">
          <CircularProgress size={22} />
          <div>
            <strong>{status === 'submitted' ? 'Checking your code against hidden inputs…' : 'Comparing your work with the solution…'}</strong>
            <div className="rg-muted">This usually takes under a minute. You can keep this page open.</div>
          </div>
        </div>
      )}

      {status === 'analysis_failed' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Your submission is saved, but feedback could not be generated right now. It will appear here
          once it's ready. You can revise and resubmit, or finish, in the meantime.
        </Alert>
      )}

      {status === 'analyzed' && feedback && (
        <div className="rg-card rg-feedback">
          <h3>Feedback on attempt {submission.attempt}</h3>
          {submission.testsTotal != null && (
            <div className="rg-feedback__tests">Hidden inputs: {submission.testsPassed} of {submission.testsTotal} handled correctly</div>
          )}
          {feedback.overall_message && <p className="rg-feedback__overall">{feedback.overall_message}</p>}
          {feedback.hints && feedback.hints.length > 0 ? (
            <ol className="rg-feedback__hints">
              {feedback.hints.map((h, i) => <li key={i}>{h.hint}</li>)}
            </ol>
          ) : (
            <p>No gaps found - nice work.</p>
          )}
        </div>
      )}

      {choosing && (
        <div className="rg-card">
          {status === 'analyzed' && !submission.feedbackRated && (
            <>
              <h3>About these hints</h3>
              <QuestionForm questions={FEEDBACK_RATING} answers={rating} onChange={setRating} />
            </>
          )}
          <h3>What would you like to do next?</h3>
          <p className="rg-muted">
            {submission.canResubmit
              ? `You can revise your code and explanation and submit again (attempt ${submission.attempt + 1} of ${submission.maxAttempts}), or finish now.`
              : `You've used all ${submission.maxAttempts} attempts.`}
          </p>
          <div className="rg-actions rg-actions--start">
            {submission.canResubmit && (
              <button
                className="ws-run-button"
                disabled={savingForm || (status === 'analyzed' && !submission.feedbackRated && !isComplete(FEEDBACK_RATING, rating))}
                onClick={() => rateThen('revise')}
              >
                Revise and resubmit
              </button>
            )}
            <button
              className={submission.canResubmit ? 'rg-secondary-button' : 'ws-run-button'}
              disabled={savingForm || (status === 'analyzed' && !submission.feedbackRated && !isComplete(FEEDBACK_RATING, rating))}
              onClick={() => rateThen('finish')}
            >
              {submission.canResubmit ? "I'm done" : 'Continue'}
            </button>
          </div>
        </div>
      )}

      {betweenAttempts && finishing && !submission.surveyDone && (
        <div className="rg-card">
          <h3>A few last questions</h3>
          <p className="rg-muted">Your answers are for research only and do not affect your grade. Please be honest.</p>
          <QuestionForm questions={aiUseSurvey} answers={survey} onChange={setSurvey} />
          <div className="rg-actions">
            {submission.canResubmit && (
              <button className="rg-secondary-button" disabled={savingForm} onClick={() => setFinishing(false)}>Back</button>
            )}
            <button className="ws-run-button" disabled={!isComplete(aiUseSurvey, survey) || savingForm} onClick={saveSurvey}>Finish</button>
          </div>
        </div>
      )}

      {submission.surveyDone && betweenAttempts && (
        <div className="rg-card rg-done">
          <h3>All done - thank you!</h3>
          <p className="rg-muted">You can close this page.</p>
        </div>
      )}
    </div>
  )
}
